import { useState } from "react"
import { OnboardingStep } from "@/hooks/use-onboarding"
import { OnboardingStepActions } from '@/components/onboarding/onboarding-step-actions'
import { FormField } from "@/components/ui/form-field"
import { Input } from "@/components/ui/input"
import { OnboardingStepNext, runOnboardingStepNext } from "@/lib/onboarding-step-nav"

interface OrganisationStepProps {
  step: OnboardingStep
  onNext: OnboardingStepNext
  onBack?: () => void
  isCompleting: boolean
  canGoBack: boolean
}

export function OrganisationStep({ onNext, onBack, isCompleting, canGoBack }: OrganisationStepProps) {
  const [organisationName, setOrganisationName] = useState("")
  const [action, setAction] = useState<'create' | 'join'>('create')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSave = async () => {
    setSaving(true)
    setError(null)

    try {
      // TODO: Implement organisation creation/joining logic
      await runOnboardingStepNext(onNext, setError)
    } catch (err) {
      console.error('Error saving organisation:', err)
      setError('Network error. Please try again.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <FormField label="Action">
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={action === 'create'}
                onChange={() => setAction('create')}
                disabled={saving || isCompleting}
              />
              Create Organisation
            </label>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={action === 'join'}
                onChange={() => setAction('join')}
                disabled={saving || isCompleting}
              />
              Join Organisation
            </label>
          </div>
        </FormField>

        <FormField
          label={action === 'create' ? 'Organisation Name' : 'Organisation Name or Code'}
          htmlFor="org-name"
        >
          <Input
            id="org-name"
            value={organisationName}
            onChange={(e) => setOrganisationName(e.target.value)}
            disabled={saving || isCompleting}
            placeholder={action === 'create' ? 'Enter organisation name' : 'Enter name or code'}
          />
        </FormField>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
      </div>

      <OnboardingStepActions
        canGoBack={canGoBack}
        onBack={onBack}
        onContinue={handleSave}
        backDisabled={saving || isCompleting}
        continueDisabled={saving || isCompleting}
        isBusy={saving}
        continueBusyLabel="Saving..."
      />
    </div>
  )
}
